import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Chip,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Grid,
    Stack,
    IconButton,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Refresh as RetryIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    History as HistoryIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import API from '../../../../../API';
import { toast } from 'sonner';

const RetryEligibilityDisplay = ({ eligibility }) => {
    if (!eligibility || !eligibility.eligibility_checks) return null;

    const getStatusIcon = (passed) => {
        if (passed) return <SuccessIcon color="success" fontSize="small" />;
        return <ErrorIcon color="error" fontSize="small" />;
    };

    const getStatusColor = (passed) => {
        if (passed) return 'success';
        return 'error';
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
                <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Retry Eligibility Analysis
            </Typography>

            <Grid container spacing={2}>
                {Object.entries(eligibility.eligibility_checks).map(([key, check]) => (
                    <Grid item xs={12} sm={6} key={key}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    {getStatusIcon(check.passed)}
                                    <Typography variant="subtitle2" sx={{ ml: 1, textTransform: 'capitalize' }}>
                                        {key.replace(/_/g, ' ')}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {check.message}
                                </Typography>
                                {check.current_attempts !== undefined && (
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                        Attempts: {check.current_attempts}/{check.max_allowed}
                                    </Typography>
                                )}
                                {check.amount !== undefined && (
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                        Amount: ${check.amount.toFixed(2)}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {eligibility.recommendations && eligibility.recommendations.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Recommendations:
                    </Typography>
                    {eligibility.recommendations.map((rec, index) => (
                        <Alert key={index} severity={eligibility.eligible ? 'success' : 'warning'} sx={{ mb: 1 }}>
                            {rec}
                        </Alert>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const RetryHistoryDisplay = ({ withdrawalId }) => {
    const { data: retryHistory, isLoading } = useQuery({
        queryKey: ['withdrawal-retry-history', withdrawalId],
        queryFn: () => API.withdrawals.getRetryHistory(withdrawalId),
        enabled: !!withdrawalId,
    });

    if (isLoading) {
        return <CircularProgress size={20} />;
    }

    if (!retryHistory || !retryHistory.retry_history || retryHistory.retry_history.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No retry attempts found.
            </Typography>
        );
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'success': return 'success';
            case 'failed': return 'error';
            case 'processing': return 'warning';
            case 'initiated': return 'info';
            default: return 'default';
        }
    };

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>
                Retry History ({retryHistory.retry_count} attempts)
            </Typography>
            {retryHistory.retry_history.map((retry, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="subtitle2">
                                    Attempt #{retry.retry_attempt_number}{retry.is_admin_retry ? ' (Admin)' : ''}
                                </Typography>
                                <Chip 
                                    label={retry.retry_status} 
                                    color={getStatusColor(retry.retry_status)}
                                    size="small"
                                    sx={{ ml: 1 }}
                                />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {new Date(retry.retry_initiated_at).toLocaleString()}
                            </Typography>
                        </Box>
                        
                        {retry.retry_reason && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <strong>Reason:</strong> {retry.retry_reason}
                            </Typography>
                        )}
                        
                        {retry.retry_result && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Result:</strong> {retry.retry_result}
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
};

export default function RetryWithdrawalDialog({ 
    open, 
    onClose, 
    withdrawalId, 
    withdrawalData,
    onRetrySuccess 
}) {
    const [retryReason, setRetryReason] = useState('');
    const [forceRetry, setForceRetry] = useState(false);
    const [showEligibility, setShowEligibility] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    
    const queryClient = useQueryClient();

    // Fetch retry eligibility
    const { data: eligibility, isLoading: eligibilityLoading, error: eligibilityError } = useQuery({
        queryKey: ['withdrawal-retry-eligibility', withdrawalId],
        queryFn: () => API.withdrawals.getRetryEligibility(withdrawalId),
        enabled: !!withdrawalId && open,
        retry: false, // Don't retry on error to prevent infinite loops
        onError: (error) => {
            console.error('Eligibility check failed:', error);
            toast.error('Failed to check retry eligibility');
        }
    });

    // Retry mutation
    const retryMutation = useMutation({
        mutationFn: (data) => API.withdrawals.retry(withdrawalId, data),
        onSuccess: (response) => {
            toast.success('Withdrawal retry initiated successfully');
            queryClient.invalidateQueries(['withdrawals']);
            queryClient.invalidateQueries(['withdrawal-retry-history', withdrawalId]);
            queryClient.invalidateQueries(['withdrawal-retry-eligibility', withdrawalId]);
            onRetrySuccess?.(response);
            onClose();
        },
        onError: (error) => {
            toast.error(error.response?.data?.error || 'Failed to retry withdrawal');
        },
    });

    const handleRetry = () => {
        if (!retryReason.trim()) {
            toast.error('Please provide a reason for the retry');
            return;
        }

        retryMutation.mutate({
            retry_reason: retryReason,
            force_retry: forceRetry
        });
    };

    const handleClose = () => {
        setRetryReason('');
        setForceRetry(false);
        setShowEligibility(false);
        setShowHistory(false);
        onClose();
    };

    const isEligible = eligibility?.eligible;
    const canRetry = isEligible || forceRetry;

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <RetryIcon sx={{ mr: 1 }} />
                    Retry Failed Withdrawal
                </Box>
                {withdrawalData && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Withdrawal ID: {withdrawalId} | Amount: ${withdrawalData.amount_no_fee}
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Button
                            variant={showEligibility ? "contained" : "outlined"}
                            startIcon={<AssessmentIcon />}
                            onClick={() => setShowEligibility(!showEligibility)}
                            size="small"
                        >
                            Eligibility Check
                        </Button>
                        <Button
                            variant={showHistory ? "contained" : "outlined"}
                            startIcon={<HistoryIcon />}
                            onClick={() => setShowHistory(!showHistory)}
                            size="small"
                        >
                            Retry History
                        </Button>
                    </Stack>

                    {showEligibility && (
                        <Accordion expanded={showEligibility} onChange={() => setShowEligibility(!showEligibility)}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Retry Eligibility Analysis</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {eligibilityLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : eligibilityError ? (
                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                        <ErrorIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
                                        <Typography color="error" variant="h6" gutterBottom>
                                            Failed to Load Eligibility
                                        </Typography>
                                        <Typography color="text.secondary" variant="body2">
                                            {eligibilityError.response?.data?.error || eligibilityError.message || 'Unknown error occurred'}
                                        </Typography>
                                        <Button 
                                            variant="outlined" 
                                            onClick={() => queryClient.invalidateQueries(['withdrawal-retry-eligibility', withdrawalId])}
                                            sx={{ mt: 2 }}
                                        >
                                            Retry
                                        </Button>
                                    </Box>
                                ) : (
                                    <RetryEligibilityDisplay eligibility={eligibility} />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {showHistory && (
                        <Accordion expanded={showHistory} onChange={() => setShowHistory(!showHistory)}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Retry History</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <RetryHistoryDisplay withdrawalId={withdrawalId} />
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                    <Alert 
                        severity={isEligible ? "success" : "warning"} 
                        sx={{ mb: 2 }}
                    >
                        {isEligible 
                            ? "This withdrawal is eligible for retry." 
                            : "This withdrawal is not eligible for retry. You can force retry if needed."
                        }
                    </Alert>

                    <TextField
                        fullWidth
                        label="Retry Reason"
                        multiline
                        rows={3}
                        value={retryReason}
                        onChange={(e) => setRetryReason(e.target.value)}
                        placeholder="Explain why you're retrying this withdrawal..."
                        sx={{ mb: 2 }}
                        required
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <input
                            type="checkbox"
                            id="force-retry"
                            checked={forceRetry}
                            onChange={(e) => setForceRetry(e.target.checked)}
                            style={{ marginRight: '8px' }}
                        />
                        <label htmlFor="force-retry">
                            <Typography variant="body2">
                                Force retry (override eligibility checks)
                            </Typography>
                        </label>
                    </Box>

                    {forceRetry && !isEligible && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Warning:</strong> You are forcing a retry on a withdrawal that failed eligibility checks. 
                                This may result in the same failure. Please ensure the underlying issue has been resolved.
                            </Typography>
                        </Alert>
                    )}
                    
                    {eligibility?.eligibility_checks?.max_attempts?.admin_retries_unlimited && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Admin Retries:</strong> You have unlimited retry attempts for this withdrawal.
                                The automated system limit of 3 attempts does not apply to admin retries.
                            </Typography>
                        </Alert>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={retryMutation.isLoading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleRetry}
                    variant="contained"
                    color={canRetry ? "primary" : "warning"}
                    disabled={retryMutation.isLoading || !retryReason.trim()}
                    startIcon={retryMutation.isLoading ? <CircularProgress size={20} /> : <RetryIcon />}
                >
                    {retryMutation.isLoading ? 'Retrying...' : canRetry ? 'Retry Withdrawal' : 'Force Retry'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
